/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const lastUpdatedDate = new Date(1683590400000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: null,
        description: null,
        lastUpdatedDate: "Tue May 09 2023 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: "hubdb",
        type: "destination",
        icon: null,
        toc: [{
          "value": "Overview",
          "depth": 0,
          "id": "overview"
        }, {
          "value": "Supported syncing",
          "depth": 0,
          "id": "supported-syncing"
        }, {
          "value": "Connect to HubDB",
          "depth": 0,
          "id": "connect-to-hubdb"
        }, {
          "value": "Authenticate with OAuth",
          "depth": 1,
          "id": "authenticate-with-oauth"
        }, {
          "value": "Sync configuration",
          "depth": 0,
          "id": "sync-configuration"
        }, {
          "value": "Tips and troubleshooting",
          "depth": 0,
          "id": "tips-and-troubleshooting"
        }, {
          "value": "Common errors",
          "depth": 1,
          "id": "common-errors"
        }, {
          "value": "Live debugger",
          "depth": 1,
          "id": "live-debugger"
        }, {
          "value": "Sync alerts",
          "depth": 1,
          "id": "sync-alerts"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      h2: "h2",
      p: "p",
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      strong: "strong",
      ul: "ul",
      li: "li",
      a: "a",
      h3: "h3",
      h4: "h4",
      img: "img",
      pre: "pre",
      code: "code",
      span: "span"
    }, _provideComponents(), props.components), {CommonErrorsPreface, TimestampTemplateMapping, LiveDebugger, SyncAlerts} = _components;
    if (!CommonErrorsPreface) _missingMdxReference("CommonErrorsPreface", true);
    if (!LiveDebugger) _missingMdxReference("LiveDebugger", true);
    if (!SyncAlerts) _missingMdxReference("SyncAlerts", true);
    if (!TimestampTemplateMapping) _missingMdxReference("TimestampTemplateMapping", true);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.h2, {
        id: "overview",
        children: "Overview"
      }), "\n", _jsx(_components.p, {
        children: "With Hightouch's HubDB integration, you no longer need to rely on others to build and maintain databases. Push and store data to tables that you can embed within websites and landing pages. Integrated into HubSpot, data is easily accessible, editable and shareable across multiple tables. You can even publish table updates in one sync."
      }), "\n", _jsx(_components.h2, {
        id: "supported-syncing",
        children: "Supported syncing"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Sync Type"
            }), _jsx(_components.th, {
              children: "Description"
            }), _jsx(_components.th, {
              children: "Supported Sync Modes"
            })]
          })
        }), _jsx(_components.tbody, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Tables"
              })
            }), _jsx(_components.td, {
              children: "Sync data to create or update tables in a HubDB."
            }), _jsx(_components.td, {
              children: "Upsert, Update"
            })]
          })
        })]
      }), "\n", _jsx(_components.h2, {
        id: "connect-to-hubdb",
        children: "Connect to HubDB"
      }), "\n", _jsx(_components.p, {
        children: "You must have one of the following HubSpot account types to use the Hightouch integration:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "CMS Hub Professional"
        }), "\n", _jsx(_components.li, {
          children: "Enterprise"
        }), "\n", _jsx(_components.li, {
          children: "Marketing Hub Professional"
        }), "\n", _jsx(_components.li, {
          children: "Enterprise with Website Add-on"
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["Go to the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/destinations?from=docs",
          children: [_jsx(_components.strong, {
            children: "Destinations"
          }), " overview page"]
        }), " and click the ", _jsx(_components.strong, {
          children: "Add destination"
        }), " button. Select ", _jsx(_components.strong, {
          children: "HubDB"
        }), ". You can then authenticate Hightouch to HubDB via ", _jsx(_components.strong, {
          children: "OAuth"
        }), "."]
      }), "\n", _jsx(_components.h3, {
        id: "authenticate-with-oauth",
        children: "Authenticate with OAuth"
      }), "\n", _jsxs(_components.p, {
        children: ["Click ", _jsx(_components.strong, {
          children: "Log in to HubDB"
        }), " and log into your HubSpot account."]
      }), "\n", _jsx(_components.p, {
        children: "Once successful, you will be redirected back to Hightouch to enter a descriptive name for your destination and complete setup."
      }), "\n", _jsx(_components.h2, {
        id: "sync-configuration",
        children: "Sync configuration"
      }), "\n", _jsxs(_components.p, {
        children: ["Once you've set up your HubDB destination and have a ", _jsx(_components.a, {
          href: "/getting-started/concepts#models",
          children: "model"
        }), " to pull data from, you can set up your sync configuration to begin syncing data. Go to the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/syncs?from=docs",
          children: [_jsx(_components.strong, {
            children: "Syncs"
          }), " overview page"]
        }), " and click the ", _jsx(_components.strong, {
          children: "Add sync"
        }), " button to begin. Then, select the relevant model and the HubDB destination you want to sync to."]
      }), "\n", _jsx(_components.p, {
        children: "In the HubDB sync configuration form, select the HubDB table you want to sync to."
      }), "\n", _jsx(_components.h4, {
        id: "record-matching",
        children: "Record matching"
      }), "\n", _jsx(_components.p, {
        children: "Hightouch requires choosing one column to define how rows in your source should be matched to records in HubDB. The field to match should be unique and the data type of the field should match the data type of the corresponding column in your model."
      }), "\n", _jsx(_components.h4, {
        id: "field-mapping",
        children: "Field mapping"
      }), "\n", _jsx(_components.p, {
        children: "You can choose which specific fields you want to sync to your HubDB table. Only the fields that you map will be exported. All other columns from your results are ignored. If you don't have a HubDB field to sync data to, you can add a new field to the table directly within HubDB. Then, refresh the HubDB fields and the new field should be available to map."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-hubdb-field-mapping.png",
          alt: "Field mapping in the Hightouch UI"
        })
      }), "\n", _jsx(_components.h4, {
        id: "delete-behavior",
        children: "Delete behavior"
      }), "\n", _jsx(_components.p, {
        children: "You can choose how to handle data points in HubDB when the corresponding rows are deleted in your source."
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Behavior"
            }), _jsx(_components.th, {
              children: "Description"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Do nothing"
              })
            }), _jsx(_components.td, {
              children: "Keep the data in HubDB"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Clear Fields"
              })
            }), _jsx(_components.td, {
              children: "Clear the mapped fields but keep the record in the HubDB table"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Delete Destination Record"
              })
            }), _jsx(_components.td, {
              children: "Delete the record from the HubDB table completely"
            })]
          })]
        })]
      }), "\n", _jsx(_components.h2, {
        id: "tips-and-troubleshooting",
        children: "Tips and troubleshooting"
      }), "\n", _jsx(_components.h3, {
        id: "common-errors",
        children: "Common errors"
      }), "\n", _jsx(CommonErrorsPreface, {}), "\n", _jsx(_components.h4, {
        id: "400---the-value-for-column-date-was-of-type-string-but-were-expecting-numeric",
        children: "400 - the value for column date was of type STRING, but we're expecting NUMERIC"
      }), "\n", _jsx(_components.p, {
        children: "The full error message looks something like this:"
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-json",
          children: [_jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"message\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), _jsx(_components.span, {
            className: "hljs-string",
            children: "\"Error when batch creating datatable rows in table 6058806 for portal 2661677: the value {type=string, value=2021/03/20 13:30} for column date was of type STRING, but we're expecting NUMERIC\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), "\n"]
        })
      }), "\n", _jsx(_components.p, {
        children: "One possible cause for this error is sending incorrectly formatted date values to HubDB."
      }), "\n", _jsxs(_components.p, {
        children: [_jsxs(_components.a, {
          href: "https://developers.hubspot.com/docs/api/cms/hubdb",
          children: ["HubDB's ", _jsx(_components.code, {
            children: "date"
          })]
        }), " data type must be a millisecond timestamp set to midnight UTC.\nA millisecond timestamp is numerical value with 13 digits, for example ", _jsx(_components.code, {
          children: "1620170285617"
        }), ", as compared to a traditional ", _jsx(_components.a, {
          href: "https://en.wikipedia.org/wiki/Unix_time",
          children: "Unix timestamp"
        }), " which has ten."]
      }), "\n", _jsx(TimestampTemplateMapping, {}), "\n", _jsx(_components.h3, {
        id: "live-debugger",
        children: "Live debugger"
      }), "\n", _jsx(LiveDebugger, {}), "\n", _jsx(_components.h3, {
        id: "sync-alerts",
        children: "Sync alerts"
      }), "\n", _jsx(SyncAlerts, {})]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
